import {numberService} from "@mytour/shared";
import type React from "react";
import type {ReactNode} from "react";
import type {FieldError, UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {FormGroup, Input, Label} from "reactstrap";
import type {InputType} from "reactstrap/types/lib/Input.js";

/**
 * Extends the reactstrap `InputType` with a custom `"decimal"` variant. The input is
 * rendered as `type="text" inputMode="decimal"` (mobile numeric keyboard preserved) and
 * the form stores the parsed numeric value — with French comma normalization. Use this
 * instead of `type="number"` for any monetary / metric input: `type="number"` silently
 * drops `,` on desktop and reports `""` on iOS Safari when a comma is typed.
 */
export type FieldType = InputType | "decimal";

type FieldProps = {
  hookForm: UseFormReturn<any>;
  disabled: boolean;
  name: string;
  placeholder?: string;
  label?: string | null;
  step?: string;
  min?: string;
  max?: string;
  id?: string;
  required?: boolean;
  type?: FieldType;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  children?: ReactNode;
  error?: FieldError;
};

export function Field({
  hookForm,
  name,
  placeholder,
  label,
  step,
  min,
  max,
  id,
  disabled,
  type = "text",
  required = false,
  onChange,
  error: superError,
  children,
}: FieldProps) {
  const {
    register,
    formState: {errors},
  } = hookForm;
  const {t: translate} = useTranslation();
  const error = superError ?? errors?.[name];
  const isDecimal = type === "decimal";
  // Render decimals as text + inputMode="decimal" so the comma reaches the onChange,
  // and parse via numberService.parseDecimalInput. Use NaN as the sentinel when the
  // buffer is empty / unparseable so zod's z.number() catches it at submit time
  // instead of silently committing 0 like the old valueAsNumber: true path did.
  const {
    onChange: superOnChange,
    onBlur,
    ref,
  } = register(name, {
    required: required && translate("entity.validation.required"),
    valueAsNumber: type === "number",
    setValueAs: isDecimal
      ? (value: unknown) => {
          if (typeof value !== "string") {
            return value;
          }
          const parsed = numberService.parseDecimalInput(value);
          return parsed === null ? Number.NaN : parsed;
        }
      : undefined,
  });
  let finalLabel = label;
  if (label === undefined) {
    finalLabel = translate(`myTourApp.common.${name}`, {defaultValue: "Not defined"});
  }
  let groupClassName = "";
  let className = "form-control";
  if (type === "checkbox") {
    className = "form-check-input";
    groupClassName = "form-check";
  }
  const htmlType: InputType = isDecimal ? "text" : type;
  return (
    <FormGroup className={`${groupClassName}`}>
      {finalLabel && (
        <Label for={`${name}Label`}>
          {finalLabel}
          {required && " *"}
        </Label>
      )}
      <Input
        id={id ? id : `${name}Label`}
        type={htmlType}
        inputMode={isDecimal ? "decimal" : undefined}
        className={`${className}${error ? " is-invalid" : ""}`}
        name={name}
        placeholder={placeholder}
        min={min}
        max={max}
        step={step}
        onBlur={onBlur}
        innerRef={ref}
        onChange={async (e) => {
          await superOnChange(e);
          onChange?.(e);
        }}
        disabled={disabled}
      />
      {error && <div className="invalid-feedback">{handleValidate()}</div>}
      {children}
    </FormGroup>
  );

  function handleValidate(): string | undefined {
    if (error?.message && typeof error.message === "string") {
      return error.message;
    }
  }
}
